import { cs } from '../../lib/currency';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { EntityRefLink, PlayerLink } from '../../components/admin/MoneyLinks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/timezone';
import TermsBadge from '../../components/shared/TermsBadge';
import SurchargeAmount from '../../components/shared/SurchargeAmount';
import PostManualCreditDialog from '../../components/shared/PostManualCreditDialog';
import SavedPaymentMethodsList from '../../components/admin/SavedPaymentMethodsList';
import { EmailActivityTab } from '../../components/admin/EmailActivityTab';
import ParentInvoicesPanel from '../../components/admin/ParentInvoicesPanel';
import {
  ArrowLeft,
  User,
  Users,
  GraduationCap,
  CreditCard,
  Mail,
  MapPin,
  Pencil,
  ExternalLink,
  FileText,
  DollarSign,
  AlertCircle,
  Receipt,
  MessageSquare,
  Plus,
  Trash2,
  Flag,
  History,
  ArrowUp,
  ArrowDown,
  Wallet,
  PauseCircle,
  PlayCircle,
  XCircle,
  AlertTriangle,
  UserX,
  UserCheck as UserCheckIcon,
  Zap,
  Loader2,
  Sparkles,
  RefreshCw,
  Undo2,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import SaveCardOnFileDialog from '../../components/admin/SaveCardOnFileDialog';

// Parent Notes Section Component
const ParentNotesSection = ({ parentId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({ content: '', note_type: 'general', is_flagged: false });
  const [saving, setSaving] = useState(false);

  const fetchNotes = async () => {
    try {
      const response = await api.get(`/parents/${parentId}/notes`);
      setNotes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [parentId]);

  const handleAddNote = async () => {
    if (!newNote.content.trim()) {
      toast.error('Please enter note content');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/parents/${parentId}/notes`, newNote);
      toast.success('Note added successfully');
      setNewNote({ content: '', note_type: 'general', is_flagged: false });
      setShowAddNote(false);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await api.delete(`/parents/${parentId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleToggleFlag = async (noteId) => {
    try {
      await api.put(`/parents/${parentId}/notes/${noteId}/flag`);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to update flag');
    }
  };

  const getNoteTypeBadge = (type) => {
    const colors = {
      general: 'bg-slate-100 text-slate-700',
      communication: 'bg-blue-100 text-blue-700',
      billing: 'bg-green-100 text-green-700',
      behavior: 'bg-purple-100 text-purple-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={`${colors[type] || colors.general} capitalize`}>{type}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Parent Notes ({notes.length})
        </CardTitle>
        <Button size="sm" onClick={() => setShowAddNote(!showAddNote)} data-testid="add-parent-note-btn">
          <Plus className="h-4 w-4 mr-1" />
          Add Note
        </Button>
      </CardHeader>
      <CardContent>
        {/* Add Note Form */}
        {showAddNote && (
          <div className="mb-6 p-4 border rounded-lg bg-slate-50">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Note Content *</Label>
                <Textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  placeholder="Enter note about this parent..."
                  rows={3}
                  data-testid="parent-note-content-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Note Type</Label>
                  <Select
                    value={newNote.note_type}
                    onValueChange={(v) => setNewNote({ ...newNote, note_type: v })}
                  >
                    <SelectTrigger data-testid="parent-note-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="communication">Communication</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="behavior">Behavior</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newNote.is_flagged}
                      onChange={(e) => setNewNote({ ...newNote, is_flagged: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-slate-600">Flag as important</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddNote(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddNote} disabled={saving} data-testid="save-parent-note-btn">
                  {saving ? 'Saving...' : 'Save Note'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Notes List */}
        {notes.length > 0 ? (
          <div className="space-y-4">
            {notes.map((note) => (
              <div
                key={note.id}
                className={`p-4 rounded-lg border ${note.is_flagged ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}
                data-testid={`parent-note-${note.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getNoteTypeBadge(note.note_type)}
                      {note.is_flagged && (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Flag className="h-3 w-3 mr-1" />
                          Flagged
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{note.content}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      By {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFlag(note.id)}
                      className={note.is_flagged ? 'text-amber-600' : 'text-slate-400'}
                      title={note.is_flagged ? 'Remove flag' : 'Flag as important'}
                    >
                      <Flag className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNote(note.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete note"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No notes for this parent yet</p>
            <p className="text-sm mt-2">Click "Add Note" to create the first note.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ParentDetail = () => {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [billingInfo, setBillingInfo] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditSummary, setCreditSummary] = useState({ current_credit_balance: 0, total_credits_issued: 0, total_credits_used: 0 });
  // New unified credit ledger (includes lane-cancel credits, manual deposits,
  // admin-billed credit consumptions). Fetched lazily when the Billing tab
  // opens — see fetchCreditLedger below.
  const [creditLedger, setCreditLedger] = useState([]);
  const [creditLedgerLoading, setCreditLedgerLoading] = useState(false);
  // iter389 — Credit Ledger is collapsed by default; admins expand on demand.
  const [creditLedgerExpanded, setCreditLedgerExpanded] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  // iter327 — Stored-vs-ledger divergence for THIS parent. Fetched
  // lazily alongside the credit ledger so the Credit Balance card can
  // render an amber warning icon when the account balance and ledger
  // sum disagree (historic data drift; see /admin/billing/credit-
  // divergence/{user_id}).
  const [creditDivergence, setCreditDivergence] = useState(null);
  // iter377 — lightweight refresh of JUST the Credit Balance card.
  const [billingCardRefreshing, setBillingCardRefreshing] = useState(false);
  // Refunds-issued-to-this-parent list. Lazy-loaded when the Billing
  // tab opens so we don't pay the round-trip on every ParentDetail
  // page view. Sorted most-recent-first server-side.
  const [refunds, setRefunds] = useState([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundsTotal, setRefundsTotal] = useState(0);
  const [sendingReceiptId, setSendingReceiptId] = useState(null);

  // Auto-pay (recurring billing) settings. Admin-only toggle that lets
  // the monthly invoice generator off-session-charge the parent's saved
  // ACH/Card. Auto-seeded to true on first enrollment but admin can
  // toggle off via the Billing tab. See backend
  // `update_parent_auto_pay_settings` + `attempt_auto_charge`.
  const [autoPaySettings, setAutoPaySettings] = useState(null);
  const [autoPayLoading, setAutoPayLoading] = useState(false);
  const [autoPaySaving, setAutoPaySaving] = useState(false);
  // Manual credit adjustment dialog state
  const [creditAdjustOpen, setCreditAdjustOpen] = useState(false);
  const [creditAdjustAmount, setCreditAdjustAmount] = useState('');
  const [creditAdjustReason, setCreditAdjustReason] = useState('');
  const [creditAdjustSaving, setCreditAdjustSaving] = useState(false);
  // iter332c v10 — Void-ledger-row modal state. Lets admins soft-delete
  // an erroneous credit_ledger row (e.g., Nikunj's "+$165 Adjusting to
  // zero" audit-log inflation). The row is marked voided with reason;
  // readers exclude it from all totals. `adjustBalance` reverses the
  // row's original delta from customer_credits.balance — leave TRUE
  // for erroneous entries, uncheck when voiding a row that was itself
  // a compensating audit correction and the balance is already right.
  const [voidRow, setVoidRow] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidAdjustBalance, setVoidAdjustBalance] = useState(true);
  const [voidSaving, setVoidSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Allow deep-linking to a specific tab via ?tab=billing (used from Billing
  // Management → customer name click).
  const initialTab = (() => {
    try {
      return new URLSearchParams(window.location.search).get('tab') || 'overview';
    } catch {
      return 'overview';
    }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  // iter307di — Deep-link from Invoice Management. The Parent name link
  // there ships `?tab=billing&enrollment=<id>&mi=<id>`. We read the `mi`
  // value and flash-highlight the matching MI row after the billing data
  // mounts. See TableRow render below (matches on payment.id ||
  // payment.monthly_invoice_id || payment.invoice_id).
  const [highlightMiId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('mi') || null;
    } catch {
      return null;
    }
  });
  // Ephemeral flag — true for ~2s after the row is scrolled into view,
  // so we can fade out the highlight ring.
  const [highlightActive, setHighlightActive] = useState(false);

  // iter307di — Scroll the deep-linked MI row into view + flash-highlight
  // it once billingInfo has loaded and we're on the Billing tab. Runs
  // exactly once after both signals are present so reloads/tab-switches
  // don't keep re-scrolling.
  const _scrolledRef = useRef(false);
  useEffect(() => {
    if (_scrolledRef.current) return;
    if (!highlightMiId) return;
    if (activeTab !== 'billing') return;
    if (!billingInfo?.recent_payments?.length) return;
    _scrolledRef.current = true;
    // setTimeout(0) lets React commit the rows before we query the DOM.
    setTimeout(() => {
      const el = document.getElementById(`mi-row-${highlightMiId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightActive(true);
        setTimeout(() => setHighlightActive(false), 2200);
      }
    }, 0);
  }, [highlightMiId, activeTab, billingInfo]);
  const [reconciling, setReconciling] = useState(false);

  // Bill Now / Bill With Credit dialog state
  const [billDialog, setBillDialog] = useState(false);
  const [billItem, setBillItem] = useState(null);
  const [billMethod, setBillMethod] = useState('saved_card');
  const [billLoading, setBillLoading] = useState(false);

  // Consolidate legacy enrollment credit → unified account credit.
  const [consolidateDialog, setConsolidateDialog] = useState(false);
  const [saveCardOpen, setSaveCardOpen] = useState(false);
  const [consolidateLoading, setConsolidateLoading] = useState(false);

  const handleConsolidateCredits = async () => {
    setConsolidateLoading(true);
    try {
      const res = await api.post(`/parents/${parentId}/billing/consolidate-credits`);
      const d = res.data || {};
      if ((d.moved || 0) === 0) {
        toast.info('Nothing to consolidate.');
      } else {
        toast.success(
          `Moved ${cs()}${Number(d.amount_moved || 0).toFixed(2)} from ${d.moved} enrollment${d.moved === 1 ? '' : 's'} into account credit (new balance: ${cs()}${Number(d.new_customer_credit_balance || 0).toFixed(2)})`,
        );
      }
      setConsolidateDialog(false);
      fetchParentDetails?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Consolidation failed');
    } finally {
      setConsolidateLoading(false);
    }
  };

  const openBillDialog = (item, method) => {
    setBillItem(item);
    setBillMethod(method);
    setBillDialog(true);
  };

  const handleConfirmBill = async () => {
    if (!billItem) return;
    setBillLoading(true);
    try {
      // The billing line item may refer to an enrollment, tournament_registration,
      // or camp_registration. Pick the right entity_type and id.
      const entityType = billItem.entity_type || (billItem.enrollment_id ? 'enrollment' : billItem.type);
      const entityId = billItem.entity_id || billItem.enrollment_id || billItem.id;
      const res = await api.post(`/parents/${parentId}/billing/charge-pending`, {
        entity_type: entityType,
        entity_id: entityId,
        amount: Number(billItem.amount || 0),
        method: billMethod,
        note: `Billed by admin for ${billItem.description}`,
      });
      const d = res.data || {};
      if (billMethod === 'credit') {
        toast.success(`Charged ${cs()}${Number(d.amount || 0).toFixed(2)} from account credit · ${cs()}${Number(d.credit_remaining || 0).toFixed(2)} left`);
      } else {
        toast.success(`Charged ${cs()}${Number(d.amount || 0).toFixed(2)} to saved card`);
      }
      setBillDialog(false);
      setBillItem(null);
      fetchParentDetails?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bill failed');
    } finally {
      setBillLoading(false);
    }
  };

  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parentForm, setParentForm] = useState({});
  
  // Enrollment action dialog
  const [enrollmentActionDialog, setEnrollmentActionDialog] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [enrollmentAction, setEnrollmentAction] = useState(null); // 'pause', 'cancel', 'resume'
  const [actionForm, setActionForm] = useState({
    pause_start_date: '',
    resume_date: '',
    cancel_date: '',
    reason: '',
    // Admin overrides on cancel (iter216) — defaulted off; the
    // Cancel dialog wires them to explicit checkboxes so the action
    // is never silent.
    override_notice_period: false,
    override_credit: false,
  });
  const [creditCalculation, setCreditCalculation] = useState(null);
  const [calculatingCredit, setCalculatingCredit] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  
  // Inactivate dialog state
  const [inactivateDialog, setInactivateDialog] = useState(false);
  const [activeEnrollmentsCheck, setActiveEnrollmentsCheck] = useState(null);
  const [loadingInactivateCheck, setLoadingInactivateCheck] = useState(false);
  const [processingInactivate, setProcessingInactivate] = useState(false);

  useEffect(() => {
    fetchParentDetails();
  }, [parentId]);

  const refreshCreditLedger = async () => {
    try {
      setCreditLedgerLoading(true);
      const res = await api.get(`/admin/customers/${parentId}/credit`);
      setCreditLedger(res.data.ledger_history || []);
      setCreditBalance(Number(res.data.balance || 0));
      // iter327 — piggyback on the ledger refresh to keep the amber
      // divergence warning in sync. Failure is silent so a missing
      // endpoint (older backend) doesn't break the Billing tab.
      try {
        const dv = await api.get(`/admin/billing/credit-divergence/${parentId}`);
        setCreditDivergence(dv.data || null);
      } catch {
        setCreditDivergence(null);
      }
    } catch {
      // swallow — existing data stays on screen
    } finally {
      setCreditLedgerLoading(false);
    }
  };

  // iter377 — Refresh ONLY the Credit Balance card (billing-info +
  // balance + divergence) without a full-page reload. Wired to the
  // refresh icon on the card.
  const refreshBillingCard = async () => {
    setBillingCardRefreshing(true);
    try {
      const billingRes = await api.get(`/parents/${parentId}/billing-info`);
      setBillingInfo(billingRes.data);
      await refreshCreditLedger();
    } catch {
      // keep existing data on screen
    } finally {
      setBillingCardRefreshing(false);
    }
  };

  // Promote this parent to be the primary parent of one of their children.
  const [makePrimaryChildId, setMakePrimaryChildId] = useState(null);
  const handleMakeChildPrimary = async (child) => {
    if (!window.confirm(
      `Make ${parent?.name || 'this parent'} the primary parent for ${child.full_name}? ` +
      `The current primary parent will be moved to additional parents.`
    )) return;
    setMakePrimaryChildId(child.id);
    try {
      const { data } = await api.post(`/admin/players/${child.id}/make-primary-parent`, {
        parent_id: parentId,
      });
      toast.success(data.message || 'Primary parent updated');
      // Refresh children list
      try {
        const childrenRes = await api.get(`/parents/${parentId}/children`);
        setChildren(childrenRes.data || []);
      } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make primary');
    } finally {
      setMakePrimaryChildId(null);
    }
  };

  const submitCreditAdjust = async () => {
    const amount = Number(creditAdjustAmount);
    if (!amount || Number.isNaN(amount)) {
      toast.error('Enter a non-zero amount (use a minus sign to debit)');
      return;
    }
    if (!creditAdjustReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      setCreditAdjustSaving(true);
      const { data } = await api.post(`/admin/customers/${parentId}/credit/adjust`, {
        amount,
        reason: creditAdjustReason.trim(),
      });
      const sign = amount > 0 ? 'Credited' : 'Debited';
      toast.success(`${sign} ${cs()}${Math.abs(amount).toFixed(2)} — new balance ${cs()}${data.balance.toFixed(2)}`);
      setCreditAdjustOpen(false);
      setCreditAdjustAmount('');
      setCreditAdjustReason('');
      await refreshCreditLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust credit');
    } finally {
      setCreditAdjustSaving(false);
    }
  };

  // Reconcile orphan paid payment_transactions to their matching unpaid
  // small_group_invoices for this parent. See backend handler for the
  // matching rules (amount equality + ±14 days created_at).
  const handleReconcileSgPayments = async () => {
    if (!parentId) return;
    if (!window.confirm(
      'Scan this parent\'s payments and auto-link any paid Stripe transactions ' +
      'to outstanding invoices across ALL categories (Small Group, Enrollments, ' +
      'Tournaments, Lane Bookings, Camps, Manual / Monthly invoices)? ' +
      'Idempotent — safe to re-run.'
    )) {
      return;
    }
    setReconciling(true);
    try {
      const { data } = await api.post(
        `/parents/${parentId}/reconcile-all`
      );
      const {
        matched = 0, ambiguous = 0, no_match = 0,
        healed_forwards = 0, by_category = {},
      } = data || {};
      const parts = [];
      if (by_category.small_group?.matched) parts.push(`${by_category.small_group.matched} SG`);
      if (by_category.enrollment?.matched) parts.push(`${by_category.enrollment.matched} enrollments`);
      if (healed_forwards) parts.push(`${healed_forwards} other invoices healed`);
      const breakdown = parts.length ? ` (${parts.join(' · ')})` : '';
      if (matched > 0) {
        toast.success(
          `Reconciled ${matched} payment${matched === 1 ? '' : 's'}${breakdown}` +
            (ambiguous ? ` · ${ambiguous} ambiguous — review manually` : '') +
            (no_match ? ` · ${no_match} unmatched` : ''),
          { duration: 8000 },
        );
      } else if (ambiguous > 0) {
        toast.warning(
          `${ambiguous} ambiguous match${ambiguous > 1 ? 'es' : ''} — review manually.`
        );
      } else {
        toast.info('Nothing to reconcile.');
      }
      // Refresh billing so UI reflects new statuses.
      try {
        const billingRes = await api.get(`/parents/${parentId}/billing-info`);
        setBillingInfo(billingRes.data);
      } catch { /* soft */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  // iter270: replaced by unified `handleReconcileSgPayments` →
  // POST /parents/{id}/reconcile-all (single endpoint covers Small
  // Group, Enrollment, Tournament, Lane, Camp, Manual & Monthly
  // invoices in one pass).
  const fetchParentDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch parent details
      const parentRes = await api.get(`/parents/${parentId}`);
      setParent(parentRes.data);
      setParentForm({
        name: parentRes.data.name || '',
        phone: parentRes.data.phone || '',
        role: parentRes.data.role || 'parent',
        address: parentRes.data.address || '',
        city: parentRes.data.city || '',
        state: parentRes.data.state || '',
        zip: parentRes.data.zip || '',
        emergency_contact_name: parentRes.data.emergency_contact_name || '',
        emergency_contact_phone: parentRes.data.emergency_contact_phone || '',
        notes: parentRes.data.notes || ''
      });
      
      // Every section loads IN PARALLEL and the page renders as soon as
      // the parent record is in — the heavy billing calls no longer block
      // (or serialise behind) each other.
      setLoading(false);
      setCreditLedgerLoading(true);
      setRefundsLoading(true);
      setAutoPayLoading(true);
      const sections = [
        () => api.get(`/parents/${parentId}/children`)
          .then((r) => setChildren(r.data || []))
          .catch(() => setChildren([])),
        () => api.get(`/enrollments?parent_id=${parentId}`)
          .then((r) => setEnrollments(r.data || []))
          .catch(() => setEnrollments([])),
        () => api.get(`/parents/${parentId}/billing-info`)
          .then((r) => setBillingInfo(r.data))
          .catch(() => setBillingInfo(null)),
        // Unified credit ledger (Credit Ledger card on the Billing tab).
        () => api.get(`/admin/customers/${parentId}/credit`)
          .then((r) => {
            setCreditLedger(r.data.ledger_history || []);
            setCreditBalance(Number(r.data.balance || 0));
          })
          .catch(() => { setCreditLedger([]); setCreditBalance(0); })
          .finally(() => setCreditLedgerLoading(false)),
        // Refunds issued to this parent across all entity types.
        () => api.get('/admin/refunds', { params: { customer_id: parentId, limit: 200 } })
          .then((r) => {
            setRefunds(r.data?.rows || []);
            setRefundsTotal(Number(r.data?.grand_total || 0));
          })
          .catch(() => { setRefunds([]); setRefundsTotal(0); })
          .finally(() => setRefundsLoading(false)),
        () => api.get(`/parents/${parentId}/auto-pay-settings`)
          .then((r) => setAutoPaySettings(r.data))
          .catch(() => setAutoPaySettings(null))
          .finally(() => setAutoPayLoading(false)),
        () => api.get(`/parents/${parentId}/credit-history`)
          .then((r) => {
            setCreditHistory(r.data.credit_history || []);
            setCreditSummary({
              current_credit_balance: r.data.current_credit_balance || 0,
              total_credits_issued: r.data.total_credits_issued || 0,
              total_credits_used: r.data.total_credits_used || 0,
            });
          })
          .catch(() => setCreditHistory([])),
      ];
      await Promise.allSettled(sections.map((fn) => fn()));
      
    } catch (error) {
      toast.error('Failed to load parent details');
      navigate('/admin/parents');
    } finally {
      setLoading(false);
    }
  };

  // iter332c v10 — Reload just the credit history + summary after
  // voiding/unvoiding a ledger row so the Billing card reflects the
  // new totals without a full page refetch.
  const reloadCreditHistory = async () => {
    try {
      const creditRes = await api.get(`/parents/${parentId}/credit-history`);
      setCreditHistory(creditRes.data.credit_history || []);
      setCreditSummary({
        current_credit_balance: creditRes.data.current_credit_balance || 0,
        total_credits_issued: creditRes.data.total_credits_issued || 0,
        total_credits_used: creditRes.data.total_credits_used || 0,
      });
    } catch (err) {
      toast.error('Failed to reload credit history');
    }
  };

  const openVoidModal = (row) => {
    setVoidRow(row);
    setVoidReason('');
    setVoidAdjustBalance(true);
  };
  const closeVoidModal = () => {
    if (voidSaving) return;
    setVoidRow(null);
    setVoidReason('');
    setVoidAdjustBalance(true);
  };
  const submitVoid = async () => {
    if (!voidRow?.id) return;
    const reason = (voidReason || '').trim();
    if (!reason) {
      toast.error('Please enter a reason for the void');
      return;
    }
    setVoidSaving(true);
    try {
      const res = await api.post(
        `/admin/billing/credit-ledger/${voidRow.id}/void`,
        { void_reason: reason, adjust_balance: voidAdjustBalance }
      );
      const bal = res.data?.new_balance;
      toast.success(
        voidAdjustBalance && bal !== null && bal !== undefined
          ? `Row voided · balance now ${cs()}${Number(bal).toFixed(2)}`
          : 'Row voided'
      );
      closeVoidModal();
      await reloadCreditHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to void row');
    } finally {
      setVoidSaving(false);
    }
  };

  const handleSendRefundReceipt = async (refund) => {
    if (!refund?.id) return;
    setSendingReceiptId(refund.id);
    try {
      const res = await api.post(`/admin/refunds/${refund.id}/send-receipt`);
      toast.success(`Receipt sent to ${res.data?.sent_to || 'customer'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setSendingReceiptId(null);
    }
  };

  const handleSaveParent = async () => {
    try {
      setSaving(true);
      await api.put(`/parents/${parentId}`, parentForm);
      const label = parentForm.role === 'customer' ? 'Customer' : 'Parent';
      toast.success(`${label} updated successfully`);
      setEditDialog(false);
      fetchParentDetails();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Inactivate parent functions
  const handleOpenInactivateDialog = async () => {
    setLoadingInactivateCheck(true);
    setInactivateDialog(true);
    try {
      const response = await api.get(`/parents/${parentId}/active-enrollments`);
      setActiveEnrollmentsCheck(response.data);
    } catch (error) {
      toast.error('Failed to check active enrollments');
      setActiveEnrollmentsCheck(null);
    } finally {
      setLoadingInactivateCheck(false);
    }
  };

  const handleInactivateParent = async () => {
    setProcessingInactivate(true);
    try {
      await api.post(`/parents/${parentId}/inactivate`);
      toast.success(`${parent.name} has been inactivated`);
      setInactivateDialog(false);
      fetchParentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to inactivate parent');
    } finally {
      setProcessingInactivate(false);
    }
  };

  const handleReactivateParent = async () => {
    try {
      await api.post(`/parents/${parentId}/reactivate`);
      toast.success(`${parent.name} has been reactivated`);
      fetchParentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate parent');
    }
  };

  // iter387 — Copy a no-login, non-expiring public payment link covering the
  // parent's whole outstanding balance (works for inactive parents too).
  const [copyingPayLink, setCopyingPayLink] = useState(false);
  const handleCopyPayLink = async () => {
    setCopyingPayLink(true);
    try {
      const { data } = await api.get(`/admin/parents/${parentId}/pay-link`);
      if (!data?.total_outstanding || data.total_outstanding <= 0) {
        toast.info('No outstanding balance — nothing to collect.');
        return;
      }
      try {
        await navigator.clipboard.writeText(data.pay_url);
        toast.success(`Payment link copied — ${cs()}${Number(data.total_outstanding).toFixed(2)} across ${data.invoice_count} invoice(s). Share it; no login needed.`);
      } catch {
        // Clipboard blocked (e.g. insecure context) — show the link to copy manually.
        window.prompt('Copy this payment link:', data.pay_url);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setCopyingPayLink(false);
    }
  };

  const getEnrollmentStatusBadge = (status) => {
    const colors = {
      current: 'bg-green-100 text-green-700',
      approved: 'bg-blue-100 text-blue-700',
      pending: 'bg-amber-100 text-amber-700',
      paused: 'bg-orange-100 text-orange-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-slate-100 text-slate-700'
    };
    return (
      <Badge className={`${colors[status] || colors.pending} capitalize`}>
        {status}
      </Badge>
    );
  };

  // Enrollment action handlers
  const openEnrollmentAction = (enrollment, action) => {
    setSelectedEnrollment(enrollment);
    setEnrollmentAction(action);
    setCreditCalculation(null);
    
    // Set default dates
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    setActionForm({
      pause_start_date: today,
      resume_date: nextMonth.toISOString().split('T')[0],
      cancel_date: '',
      reason: '',
      override_notice_period: false,
      override_credit: false,
    });
    
    setEnrollmentActionDialog(true);
    
    // Auto-calculate credit for cancel
    if (action === 'cancel') {
      calculateCancelCredit(enrollment.id);
    }
  };

  const calculatePauseCredit = async () => {
    if (!selectedEnrollment || !actionForm.pause_start_date) return;
    
    setCalculatingCredit(true);
    try {
      const response = await api.get(`/enrollments/${selectedEnrollment.id}/calculate-pause-credit?pause_date=${actionForm.pause_start_date}`);
      setCreditCalculation(response.data);
    } catch (error) {
      console.error('Failed to calculate credit:', error);
      toast.error('Failed to calculate credit');
    } finally {
      setCalculatingCredit(false);
    }
  };

  const calculateCancelCredit = async (enrollmentId, cancelDate = null) => {
    setCalculatingCredit(true);
    try {
      let url = `/enrollments/${enrollmentId || selectedEnrollment.id}/calculate-cancel-credit`;
      if (cancelDate) {
        url += `?cancel_date=${cancelDate}`;
      }
      const response = await api.get(url);
      setCreditCalculation(response.data);
      
      // Set the earliest cancel date as default if not set
      if (!actionForm.cancel_date) {
        setActionForm(prev => ({
          ...prev,
          cancel_date: response.data.earliest_cancel_date
        }));
      }
    } catch (error) {
      console.error('Failed to calculate credit:', error);
      toast.error('Failed to calculate credit');
    } finally {
      setCalculatingCredit(false);
    }
  };

  const handleEnrollmentAction = async () => {
    if (!selectedEnrollment) return;
    
    setProcessingAction(true);
    try {
      let response;
      
      if (enrollmentAction === 'pause') {
        if (!actionForm.pause_start_date || !actionForm.resume_date) {
          toast.error('Please provide pause start date and resume date');
          setProcessingAction(false);
          return;
        }
        response = await api.post(`/enrollments/${selectedEnrollment.id}/admin-pause`, {
          pause_start_date: actionForm.pause_start_date,
          resume_date: actionForm.resume_date,
          reason: actionForm.reason
        });
      } else if (enrollmentAction === 'cancel') {
        // Reason mandatory (iter216 + production hotfix Feb 2026).
        if (!(actionForm.reason || '').trim()) {
          toast.error('Reason is required to cancel an enrollment');
          setProcessingAction(false);
          return;
        }
        response = await api.post(`/enrollments/${selectedEnrollment.id}/admin-cancel`, {
          cancel_date: actionForm.cancel_date || null,
          reason: actionForm.reason,
          override_notice_period: !!actionForm.override_notice_period,
          override_credit: !!actionForm.override_credit,
        });
      } else if (enrollmentAction === 'resume') {
        response = await api.post(`/enrollments/${selectedEnrollment.id}/admin-resume`, {
          resume_date: actionForm.resume_date || null,
          reason: actionForm.reason
        });
      }
      
      toast.success(response.data.message || 'Action completed successfully');
      setEnrollmentActionDialog(false);
      fetchParentDetails(); // Refresh data
      
    } catch (error) {
      console.error('Failed to process action:', error);
      toast.error(error.response?.data?.detail || 'Failed to process action');
    } finally {
      setProcessingAction(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Parent Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!parent) {
    return (
      <DashboardLayout title="Parent Details">
        <div className="text-center py-12">
          <p className="text-slate-500">Parent not found</p>
          <Button onClick={() => navigate('/admin/parents')} className="mt-4">
            Back to Parents
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Parent Details">
      <div className="space-y-6" data-testid="parent-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin/parents')}
              data-testid="back-to-parents"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-xl">
                  {parent.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-900">{parent.name}</h1>
                  {parent.numeric_id && (
                    <Badge variant="outline" className="text-xs font-mono bg-slate-100">
                      Parent ID: {parent.numeric_id}
                    </Badge>
                  )}
                  {parent.is_active === false && (
                    <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                  )}
                  <TermsBadge
                    termsAcceptedAt={parent.terms_accepted_at}
                    termsLastAcceptedAt={parent.terms_last_accepted_at}
                    termsSource={parent.terms_source}
                    testId="parent-terms-badge"
                  />
                </div>
                <p className="text-slate-500">{parent.email}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleCopyPayLink}
              disabled={copyingPayLink}
              data-testid="copy-parent-pay-link-btn"
              title="Copy a no-login link to pay the full outstanding balance (works for inactive parents)"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {copyingPayLink ? 'Preparing…' : 'Copy Payment Link'}
            </Button>
            <Button onClick={() => setEditDialog(true)} data-testid="edit-parent-btn">
              <Pencil className="h-4 w-4 mr-2" />
              Edit Parent
            </Button>

            {/* iter271: secondary admin actions collapsed into a popover
                so the header stays uncluttered on narrower viewports.
                Matches the "Repair Tools" popover pattern used on the
                Tournament detail page. */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-slate-600 hover:text-slate-800"
                  data-testid="parent-actions-trigger"
                  aria-label="More parent actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-60 p-2 space-y-1"
                data-testid="parent-actions-menu"
              >
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide px-2 pt-1 pb-1">
                  More actions
                </p>
                <div className="flex flex-col gap-1">
                  {parent.is_active === false ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReactivateParent}
                      className="justify-start text-green-700 hover:bg-green-50"
                      data-testid="reactivate-parent-btn"
                    >
                      <UserCheckIcon className="h-4 w-4 mr-2" />
                      Reactivate parent
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleOpenInactivateDialog}
                      className="justify-start text-red-700 hover:bg-red-50"
                      data-testid="inactivate-parent-btn"
                    >
                      <UserX className="h-4 w-4 mr-2" />
                      Inactivate parent
                    </Button>
                  )}
                  {/* iter239: post manual credit (admin + staff). Rendered
                      as a popover menu item using its built-in `trigger`
                      slot — keeps the dialog state local to the
                      PostManualCreditDialog component. */}
                  <PostManualCreditDialog
                    userId={parent.id}
                    userName={parent.name}
                    userRole="parent"
                    onPosted={fetchParentDetails}
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start text-emerald-700 hover:bg-emerald-50 w-full"
                        data-testid="post-manual-credit-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Post manual credit
                      </Button>
                    }
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full max-w-3xl overflow-x-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="children" data-testid="tab-children">Children ({children.length})</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Name</p>
                      <p className="font-medium">{parent.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <a href={`mailto:${parent.email}`} className="font-medium text-blue-600 hover:underline">
                        {parent.email}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      {parent.phone ? (
                        <a href={`tel:${parent.phone}`} className="font-medium text-blue-600 hover:underline">
                          {parent.phone}
                        </a>
                      ) : (
                        <span className="text-slate-400">Not provided</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Account Status</p>
                      <Badge className={parent.is_active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                        {parent.is_active === false ? 'Inactive' : 'Active'}
                      </Badge>
                    </div>
                  </div>
                  
                  {(parent.address || parent.city) && (
                    <div className="pt-4 border-t">
                      <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        Address
                      </p>
                      <p className="text-slate-700">
                        {parent.address && <span>{parent.address}<br /></span>}
                        {parent.city && <span>{parent.city}, </span>}
                        {parent.state && <span>{parent.state} </span>}
                        {parent.zip && <span>{parent.zip}</span>}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Emergency Contact */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Emergency Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {parent.emergency_contact_name || parent.emergency_contact_phone ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Name</p>
                        <p className="font-medium">{parent.emergency_contact_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Phone</p>
                        {parent.emergency_contact_phone ? (
                          <a href={`tel:${parent.emergency_contact_phone}`} className="font-medium text-blue-600 hover:underline">
                            {parent.emergency_contact_phone}
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No emergency contact provided</p>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Quick Stats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{children.length}</p>
                      <p className="text-sm text-blue-600">Children</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">
                        {enrollments.filter(e => ['current', 'approved', 'active'].includes(e.status)).length}
                      </p>
                      <p className="text-sm text-green-600">Active Enrollments</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-600">
                        {new Date(parent.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-slate-600">Member Since</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              {parent.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Admin Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 whitespace-pre-wrap">{parent.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Children Tab */}
          <TabsContent value="children" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Children
                </CardTitle>
              </CardHeader>
              <CardContent>
                {children.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {children.map((child) => {
                      const isPrimary = child.parent_id === parentId;
                      return (
                      <Card key={child.id} className="border">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                <span className="text-blue-600 font-bold">
                                  {child.full_name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <Link
                                  to={`/admin/players/${child.id}`}
                                  className="font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                  data-testid={`child-link-${child.id}`}
                                >
                                  {child.full_name}
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                                <p className="text-sm text-slate-500 flex items-center gap-2">
                                  {child.age} years • {child.skill_level || 'Beginner'}
                                  {isPrimary ? (
                                    <Badge variant="outline" className="text-[10px] py-0 px-1.5">Primary</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-slate-50">Additional</Badge>
                                  )}
                                </p>
                              </div>
                            </div>
                            {!isPrimary && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMakeChildPrimary(child)}
                                disabled={makePrimaryChildId === child.id}
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs whitespace-nowrap"
                                data-testid={`make-primary-${child.id}`}
                                title="Make this parent the primary parent for this child"
                              >
                                {makePrimaryChildId === child.id ? <span className="animate-spin">⏳</span> : 'Make Primary'}
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No children registered yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  All Enrollments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {enrollments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Enrollment ID</TableHead>
                          <TableHead>Child</TableHead>
                          <TableHead>Program</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Start</TableHead>
                          <TableHead>End</TableHead>
                          <TableHead>Fee</TableHead>
                          <TableHead>Frequency</TableHead>
                          <TableHead>Next Bill</TableHead>
                          <TableHead>Last Paid</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enrollments.map((enrollment) => {
                          const hasEndDate = enrollment.cancel_date || enrollment.end_date;
                          const isPast = hasEndDate && ['cancelled', 'canceled', 'completed'].includes(enrollment.status?.toLowerCase());
                          const canPause = ['current', 'active', 'approved'].includes(enrollment.status?.toLowerCase());
                          const canResume = enrollment.status?.toLowerCase() === 'paused';
                          const canCancel = ['current', 'active', 'approved', 'paused'].includes(enrollment.status?.toLowerCase());
                          const freq = enrollment.payment_frequency || 'monthly';
                          const freqLabel = freq === 'quarterly' ? 'Quarterly' : freq === 'biannual' ? 'Bi-Annual' : 'Monthly';
                          const freqMult = freq === 'quarterly' ? 3 : freq === 'biannual' ? 6 : 1;

                          return (
                            <TableRow key={enrollment.id} className={isPast ? 'opacity-60' : ''} data-testid={`enrollment-row-${enrollment.id}`}>
                              <TableCell className="font-mono text-xs">
                                <Link
                                  to={`/admin/enrollments/${enrollment.id}`}
                                  className="text-blue-600 hover:underline"
                                  data-testid={`enrollment-id-link-${enrollment.id}`}
                                  title={enrollment.id}
                                >
                                  {enrollment.id ? enrollment.id.slice(0, 8) : '-'}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Link
                                  to={`/admin/players/${enrollment.player_id}`}
                                  className="text-blue-600 hover:underline"
                                >
                                  {enrollment.player_name}
                                </Link>
                              </TableCell>
                              <TableCell className="font-medium">
                                {enrollment.program_name}
                              </TableCell>
                              <TableCell>{(enrollment.class_names && enrollment.class_names.length > 0) ? (
                                <div className="flex flex-wrap gap-1" data-testid={`enrollment-classes-${enrollment.id}`}>
                                  {enrollment.class_names.map((name, idx) => (
                                    <Badge
                                      key={(enrollment.class_ids && enrollment.class_ids[idx]) || `${enrollment.id}-${idx}`}
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (enrollment.class_name || '-')}</TableCell>
                              <TableCell>
                                {isPast ? (
                                  <Badge className="bg-slate-100 text-slate-600">Past</Badge>
                                ) : (
                                  getEnrollmentStatusBadge(enrollment.status)
                                )}
                              </TableCell>
                              <TableCell>
                                {enrollment.start_date ? formatDateOnly(enrollment.start_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' }) : '-'}
                              </TableCell>
                              <TableCell>
                                {hasEndDate ? (
                                  <span className="text-slate-600">
                                    {formatDateOnly(enrollment.cancel_date || enrollment.end_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell data-testid={`enr-fee-${enrollment.id}`}>
                                {enrollment.monthly_fee != null ? `${cs()}${Number(enrollment.monthly_fee).toFixed(2)}` : '-'}
                                {enrollment.credit_balance > 0 && (
                                  <div className="text-xs text-green-600">
                                    Credit: {cs()}{enrollment.credit_balance.toFixed(2)}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm">{freqLabel}</span>
                                {freqMult > 1 && (
                                  <span className="text-[10px] text-slate-400 block">× {freqMult} months</span>
                                )}
                              </TableCell>
                              <TableCell data-testid={`enr-next-bill-${enrollment.id}`}>
                                {enrollment.next_billing_date
                                  ? formatDateOnly(enrollment.next_billing_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })
                                  : '-'}
                              </TableCell>
                              <TableCell data-testid={`enr-last-paid-${enrollment.id}`}>
                                {enrollment.last_payment_date ? (
                                  <>
                                    <span className="text-sm">
                                      {formatDateOnly(enrollment.last_payment_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}
                                    </span>
                                    {enrollment.last_payment_amount != null && (
                                      <span className="text-[10px] text-slate-400 block">
                                        {cs()}{Number(enrollment.last_payment_amount).toFixed(2)}
                                      </span>
                                    )}
                                  </>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                {!isPast && (
                                  <div className="flex items-center justify-end gap-1">
                                    {canPause && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openEnrollmentAction(enrollment, 'pause')}
                                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                        title="Pause Enrollment"
                                        data-testid={`pause-btn-${enrollment.id}`}
                                      >
                                        <PauseCircle className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {canResume && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openEnrollmentAction(enrollment, 'resume')}
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                        title="Resume Enrollment"
                                        data-testid={`resume-btn-${enrollment.id}`}
                                      >
                                        <PlayCircle className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {canCancel && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openEnrollmentAction(enrollment, 'cancel')}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        title="Cancel Enrollment"
                                        data-testid={`cancel-btn-${enrollment.id}`}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <GraduationCap className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No enrollments yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="mt-6">
            <div className="space-y-6">
              {/* Credit Ledger — every credit movement in/out for this customer */}
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <CardTitle
                        className="flex items-center gap-2 cursor-pointer select-none"
                        onClick={() => setCreditLedgerExpanded((v) => !v)}
                        data-testid="credit-ledger-toggle"
                      >
                        {creditLedgerExpanded
                          ? <ChevronDown className="h-4 w-4 text-slate-400" />
                          : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        <Wallet className="h-5 w-5" />
                        Credit Ledger
                        <span className="text-sm font-normal text-slate-500 ml-2">
                          Balance: <span className="font-semibold text-emerald-600">{cs()}{creditBalance.toFixed(2)}</span>
                        </span>
                      </CardTitle>
                      <CardDescription>
                        Every credit issued or applied to this customer's account. Cancellations with
                        "Account credit" mode appear here automatically.
                      </CardDescription>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => {
                        setCreditAdjustAmount('');
                        setCreditAdjustReason('');
                        setCreditAdjustOpen(true);
                      }}
                      data-testid="adjust-credit-btn"
                    >
                      <Plus className="h-4 w-4" />
                      Adjust
                    </Button>
                  </div>
                </CardHeader>
                {creditLedgerExpanded && (
                <CardContent>
                  {creditLedgerLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  ) : creditLedger.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">
                      No credit activity yet.
                    </p>
                  ) : (
                    <div className="divide-y divide-slate-100 -mx-6">
                      {creditLedger.map((row) => {
                        const delta = Number(row.delta || 0);
                        const isCredit = delta > 0;
                        const label = row.reason || row.note ||
                          `${(row.entity_type || 'account').replace(/_/g, ' ')}`;
                        return (
                          <div
                            key={row.id}
                            className="px-6 py-3 flex items-start justify-between gap-4"
                            data-testid={`admin-ledger-row-${row.id}`}
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {label}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {(row.created_at || '').slice(0, 19).replace('T', ' ')}
                                {row.actor_name && ` · by ${row.actor_name}`}
                                {row.entity_type && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">
                                    {String(row.entity_type).replace(/_/g, ' ')}
                                  </span>
                                )}
                              </p>
                            </div>
                            <div
                              className={`text-sm font-semibold whitespace-nowrap ${
                                isCredit ? 'text-emerald-600' : 'text-orange-600'
                              }`}
                            >
                              {isCredit ? '+' : '−'}{cs()}{Math.abs(delta).toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
                )}
              </Card>

              {/* Billing Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Billing Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {billingInfo ? (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg" data-testid="parent-billing-total-paid">
                        <p className="text-2xl font-bold text-green-600">
                          {cs()}{billingInfo.total_paid?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-sm text-green-600">Total Paid</p>
                      </div>
                      {/* iter334w — Refunded is now its own summary
                          card (was a sub-line under Total Paid). Makes
                          it visible even when $0 and gives it space to
                          show a breakdown-per-source later. */}
                      <div
                        className="text-center p-4 bg-rose-50 rounded-lg"
                        data-testid="parent-billing-refunded-card"
                      >
                        <p className="text-2xl font-bold text-rose-600">
                          {cs()}{(billingInfo.total_refunded || 0).toFixed(2)}
                        </p>
                        <p className="text-sm text-rose-600">Refunded</p>
                      </div>
                      <div className="text-center p-4 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">
                          {cs()}{billingInfo.outstanding_balance?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-sm text-amber-600">Outstanding</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">
                          {cs()}{billingInfo.monthly_recurring?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-sm text-blue-600">Monthly Recurring</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-600 flex items-center justify-center gap-1.5">
                          {cs()}{(billingInfo.customer_credit_balance || 0).toFixed(2)}
                          {/* iter327 — amber warning icon when the
                              stored `customer_credits.balance` and
                              the `credit_ledger` sum disagree. Points
                              admins to reconcile before making
                              adjustments that would target the wrong
                              pool. */}
                          {creditDivergence && creditDivergence.divergent && (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="cursor-help inline-flex"
                                    data-testid="credit-divergence-warning"
                                  >
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="bg-slate-900 text-white text-[11px] max-w-xs"
                                  data-testid="credit-divergence-tooltip"
                                >
                                  <div className="space-y-1">
                                    <div className="font-semibold text-amber-300">
                                      Credit balance vs ledger divergence
                                    </div>
                                    <div className="text-slate-300">
                                      Stored balance: {cs()}{Number(creditDivergence.stored_balance || 0).toFixed(2)}
                                      <br />
                                      Ledger sum: {cs()}{Number(creditDivergence.ledger_sum || 0).toFixed(2)}
                                      <br />
                                      Diff: <span className="font-mono">{creditDivergence.diff > 0 ? '+' : ''}{cs()}{Number(creditDivergence.diff || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="text-slate-400 pt-1 border-t border-slate-700">
                                      Reconcile before adjusting so
                                      writes target the correct pool.
                                      Admin ▸ Billing ▸ Credit
                                      Divergence Reconcile.
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </p>
                        <p className="text-sm text-purple-600 flex items-center justify-center gap-1.5">
                          Credit Balance
                          <button
                            type="button"
                            onClick={refreshBillingCard}
                            disabled={billingCardRefreshing}
                            className="inline-flex items-center text-purple-400 hover:text-purple-700 disabled:opacity-50"
                            data-testid="refresh-credit-balance-btn"
                            title="Refresh credit balance"
                          >
                            <RefreshCw className={`h-3 w-3 ${billingCardRefreshing ? 'animate-spin' : ''}`} />
                          </button>
                        </p>
                        {(billingInfo.credit_balance || 0) > 0.005 && (
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            {cs()}{Number(billingInfo.credit_balance).toFixed(2)} on enrollments
                            {(billingInfo.customer_credit_balance || 0) > 0.005 && (
                              <> + {cs()}{Number(billingInfo.customer_credit_balance).toFixed(2)} account</>
                            )}
                          </p>
                        )}
                        {(billingInfo.credit_balance || 0) > 0.005 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[11px] text-purple-700 hover:bg-purple-100 mt-1"
                            onClick={() => setConsolidateDialog(true)}
                            data-testid="consolidate-credits-btn"
                          >
                            <Sparkles className="h-3 w-3 mr-1" /> Consolidate
                          </Button>
                        )}
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-600">
                          {billingInfo.payment_method || 'N/A'}
                        </p>
                        <p className="text-sm text-slate-600">Payment Method</p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => setSaveCardOpen(true)}
                          data-testid="parent-detail-save-card-btn"
                        >
                          <CreditCard className="h-3 w-3 mr-1" />
                          {billingInfo?.has_saved_payment_method ? 'Add Card / ACH' : 'Save Card / ACH'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No billing information available</p>
                  )}
                </CardContent>
              </Card>

              {/* iter272: full Invoice Management actions per row (View /
                  Edit / Send Reminder / Charge / Mark Paid Offline / Void /
                  Unvoid / Refund / Send Receipt). Replaces the previous
                  inline mini-table that only exposed "Bill with Credit"
                  and "Bill Now" — those flows still live on the action
                  bar above and as the per-invoice "Charge" / "Offline"
                  buttons inside this panel. */}
              <ParentInvoicesPanel
                parentId={parentId}
                parentEmail={parent?.email}
              />

              {/* Credit History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Credit History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Credit Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xl font-bold text-green-600" data-testid="admin-credit-balance">
                        {cs()}{creditSummary.current_credit_balance.toFixed(2)}
                      </p>
                      <p className="text-xs text-green-600">Current Balance</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xl font-bold text-blue-600">
                        {cs()}{creditSummary.total_credits_issued.toFixed(2)}
                      </p>
                      <p className="text-xs text-blue-600">Total Issued</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xl font-bold text-slate-600">
                        {cs()}{creditSummary.total_credits_used.toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-600">Total Used</p>
                    </div>
                  </div>

                  {/* Credit History List */}
                  {creditHistory.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {creditHistory.map((credit) => (
                        <div 
                          key={credit.id}
                          className={`p-3 rounded-lg border ${
                            credit.type === 'issued' 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-orange-50 border-orange-200'
                          }`}
                          data-testid={`admin-credit-${credit.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-full ${
                                credit.type === 'issued' ? 'bg-green-100' : 'bg-orange-100'
                              }`}>
                                {credit.type === 'issued' ? (
                                  <ArrowUp className="h-4 w-4 text-green-600" />
                                ) : (
                                  <ArrowDown className="h-4 w-4 text-orange-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 text-sm">
                                  {credit.description}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {credit.player_name}
                                  {credit.admin_name && ` • By ${credit.admin_name}`}
                                </p>
                                {credit.reason && (
                                  <p className="text-xs text-slate-400 mt-1">
                                    Reason: {credit.reason}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-semibold ${
                                credit.type === 'issued' ? 'text-green-600' : 'text-orange-600'
                              }`}>
                                {credit.type === 'issued' ? '+' : '-'}{cs()}{Math.abs(credit.amount).toFixed(2)}
                              </p>
                              <p className="text-xs text-slate-400">
                                {credit.timestamp 
                                  ? new Date(credit.timestamp).toLocaleDateString()
                                  : '-'
                                }
                              </p>
                              {/* iter332c v10 — Void button only on
                                  voidable rows (credit_ledger-sourced).
                                  Refunds and enrollment_history entries
                                  are sources of truth and cannot be
                                  voided from this UI. */}
                              {credit.voidable && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 mt-1 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => openVoidModal(credit)}
                                  data-testid={`void-credit-row-${credit.id}`}
                                  title="Void this ledger row (soft delete with documentation)"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Void
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-500">
                      <History className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No credit history</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* iter241: Saved Payment Methods (live from Stripe). Many
                  parents have saved PMs that admins previously couldn't
                  see because the original `/payment-methods` endpoint
                  locked to current_user. New admin variant pulls by
                  user_id, so admins finally have visibility. */}
              <Card data-testid="saved-payment-methods-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-indigo-600" />
                    Saved Payment Methods
                  </CardTitle>
                  <CardDescription>
                    Cards and bank accounts the parent has saved on file.
                    Pulled live from Stripe.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <SavedPaymentMethodsList userId={parent.id} onChanged={fetchParentDetails} />
                </CardContent>
              </Card>

              {/* Auto-Pay Settings — admin-editable opt-in. Once toggled
                  ON, the monthly invoice generator off-session-charges
                  the parent's saved Stripe payment method (ACH first,
                  card fallback). Parents see this as read-only; only
                  admins can flip it. Auto-seeded to ON when a new
                  enrollment is created. */}
              <Card data-testid="autopay-settings-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-emerald-600" />
                    Auto-Pay (Recurring Billing)
                  </CardTitle>
                  <CardDescription>
                    When enabled, next month's invoice is automatically billed
                    on the saved payment method. Admin-only setting.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {autoPayLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  ) : !autoPaySettings ? (
                    <div className="text-sm text-slate-500">Failed to load auto-pay settings.</div>
                  ) : (
                    <div className="space-y-4">
                      {/* Method on file — read-only, derived from Stripe */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg border bg-slate-50">
                          <div className="text-xs text-slate-500 mb-1">Payment Method on File</div>
                          <div className="font-medium text-slate-900 flex items-center gap-2" data-testid="autopay-method-detected">
                            {autoPaySettings.detected_methods === 'both' && (
                              <>
                                <Badge className="bg-emerald-100 text-emerald-700">ACH</Badge>
                                <Badge className="bg-indigo-100 text-indigo-700">Card</Badge>
                              </>
                            )}
                            {autoPaySettings.detected_methods === 'ach' && (
                              <Badge className="bg-emerald-100 text-emerald-700">ACH (Bank)</Badge>
                            )}
                            {autoPaySettings.detected_methods === 'card' && (
                              <Badge className="bg-indigo-100 text-indigo-700">Card</Badge>
                            )}
                            {autoPaySettings.detected_methods === 'none' && (
                              <span className="text-slate-500 text-sm">No payment method saved</span>
                            )}
                          </div>
                          {autoPaySettings.detected_methods !== 'none' && (
                            <p className="text-xs text-slate-500 mt-1">
                              Auto-pay always tries ACH first, then card.
                            </p>
                          )}
                        </div>

                        {/* Admin-set method label (what admin recorded) */}
                        <div className="p-3 rounded-lg border bg-slate-50">
                          <div className="text-xs text-slate-500 mb-1">Admin-Recorded Method</div>
                          <select
                            className="w-full text-sm border rounded px-2 py-1.5 bg-white"
                            value={autoPaySettings.auto_pay_method_on_file || ''}
                            onChange={(e) =>
                              setAutoPaySettings({
                                ...autoPaySettings,
                                auto_pay_method_on_file: e.target.value || null,
                              })
                            }
                            data-testid="autopay-method-select"
                          >
                            <option value="">— Not set —</option>
                            <option value="ach">ACH (Bank)</option>
                            <option value="card">Card</option>
                            <option value="both">Both (ACH + Card)</option>
                            <option value="none">None</option>
                          </select>
                          <p className="text-xs text-slate-500 mt-1">
                            Optional preference label — billing always tries ACH first.
                          </p>
                        </div>
                      </div>

                      {/* Auto-pay acknowledgement toggle */}
                      <label
                        className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${
                          autoPaySettings.auto_pay_acknowledged
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                        data-testid="autopay-toggle-block"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 text-emerald-600 rounded"
                          checked={!!autoPaySettings.auto_pay_acknowledged}
                          onChange={(e) =>
                            setAutoPaySettings({
                              ...autoPaySettings,
                              auto_pay_acknowledged: e.target.checked,
                            })
                          }
                          data-testid="autopay-toggle"
                        />
                        <span className="text-sm">
                          <span className="font-medium">
                            Parent has confirmed auto-pay for enrollments
                          </span>
                          <span className="block text-xs text-slate-600 mt-0.5">
                            Once checked, next month's invoice will be billed automatically.
                            Card charges include the 3% surcharge; ACH does not.
                          </span>
                          {autoPaySettings.auto_pay_acknowledged_at && (
                            <span className="block text-xs text-slate-500 mt-1">
                              Last set: {new Date(autoPaySettings.auto_pay_acknowledged_at).toLocaleString()}
                            </span>
                          )}
                        </span>
                      </label>

                      {!autoPaySettings.has_stripe_customer && autoPaySettings.auto_pay_acknowledged && (
                        <div
                          className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
                          data-testid="autopay-no-stripe-warning"
                        >
                          ⚠️ Parent doesn't have a Stripe customer yet. Auto-pay will skip until they save a payment method.
                        </div>
                      )}

                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              setAutoPaySaving(true);
                              await api.put(`/parents/${parentId}/auto-pay-settings`, {
                                auto_pay_acknowledged: autoPaySettings.auto_pay_acknowledged,
                                auto_pay_method_on_file: autoPaySettings.auto_pay_method_on_file,
                              });
                              toast.success('Auto-pay settings saved');
                              // Refresh to pick up updated audit timestamps
                              const apRes = await api.get(`/parents/${parentId}/auto-pay-settings`);
                              setAutoPaySettings(apRes.data);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to save auto-pay settings');
                            } finally {
                              setAutoPaySaving(false);
                            }
                          }}
                          disabled={autoPaySaving}
                          data-testid="autopay-save-btn"
                        >
                          {autoPaySaving ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving…</>) : 'Save Auto-Pay Settings'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment History — grouped per type so admins can easily
                  see "what coaching was paid for", "what lane rentals were
                  paid for" etc, instead of one flat blob. */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="h-5 w-5" />
                      Recent Payments
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReconcileSgPayments}
                      disabled={reconciling}
                      data-testid="reconcile-all-payments-btn"
                      title="Auto-link this parent's paid Stripe transactions across ALL payment categories (Small Group, Enrollments, Tournaments, Lane Bookings, Camps, Manual & Monthly invoices)"
                    >
                      <RefreshCw className={`h-4 w-4 mr-1.5 ${reconciling ? 'animate-spin' : ''}`} />
                      {reconciling ? 'Reconciling…' : 'Reconcile All Payments'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {billingInfo?.recent_payments && billingInfo.recent_payments.length > 0 ? (
                    (() => {
                      const TYPE_ORDER = [
                        { key: 'enrollment', label: 'Coaching · Enrollments' },
                        { key: 'small_group', label: 'Coaching · Small Group / 1:1' },
                        { key: 'tournament', label: 'Tournaments' },
                        { key: 'lane_booking', label: 'Lane Rentals' },
                        { key: 'other', label: 'Other Payments' },
                      ];
                      const groups = {};
                      for (const p of billingInfo.recent_payments) {
                        const key = TYPE_ORDER.find((t) => t.key === p.type) ? p.type : 'other';
                        (groups[key] = groups[key] || []).push(p);
                      }
                      const visibleSections = TYPE_ORDER.filter((t) => (groups[t.key] || []).length > 0);
                      return (
                        <div className="overflow-x-auto" data-testid="recent-payments-grouped">
                          <Table className="table-fixed w-full">
                            <colgroup>
                              <col style={{ width: '110px' }} />
                              <col />
                              <col style={{ width: '120px' }} />
                              <col style={{ width: '110px' }} />
                              <col style={{ width: '120px' }} />
                            </colgroup>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Method</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {visibleSections.map((t, sIdx) => {
                                const rows = groups[t.key];
                                const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                                return (
                                  <React.Fragment key={t.key}>
                                    <TableRow
                                      className="bg-slate-50 hover:bg-slate-50 border-t-2 border-slate-200"
                                      data-testid={`recent-payments-section-${t.key}`}
                                    >
                                      <TableCell colSpan={3} className="py-2">
                                        <span className="text-sm font-semibold text-slate-700">{t.label}</span>{' '}
                                        <span className="text-xs font-normal text-slate-400">
                                          ({rows.length} payment{rows.length === 1 ? '' : 's'})
                                        </span>
                                      </TableCell>
                                      <TableCell className="py-2 text-right font-semibold text-emerald-700">
                                        {cs()}{total.toFixed(2)}
                                      </TableCell>
                                      <TableCell className="py-2" />
                                    </TableRow>
                                    {rows.map((payment, idx) => {
                                      // iter307di — Anchor + flash-highlight
                                      // the deep-linked MI row when arriving
                                      // from Invoice Management (?mi=<id>).
                                      const matchesHighlight = highlightMiId && (
                                        payment.id === highlightMiId ||
                                        payment.monthly_invoice_id === highlightMiId ||
                                        payment.invoice_id === highlightMiId
                                      );
                                      const isFlashing = matchesHighlight && highlightActive;
                                      return (
                                      <TableRow
                                        key={payment.id || `${t.key}-${idx}`}
                                        id={payment.id ? `mi-row-${payment.id}` : undefined}
                                        className={isFlashing
                                          ? 'transition-colors duration-1000 bg-amber-100 ring-2 ring-amber-400'
                                          : matchesHighlight
                                            ? 'transition-colors duration-1000'
                                            : ''}
                                        data-testid={matchesHighlight ? 'parent-billing-highlighted-row' : undefined}
                                      >
                                        <TableCell className="whitespace-nowrap text-slate-600">
                                          {payment.date ? new Date(payment.date).toLocaleDateString() : '-'}
                                        </TableCell>
                                        <TableCell className="truncate">{payment.description}</TableCell>
                                        <TableCell className="capitalize text-slate-600">
                                          {payment.method || 'stripe'}
                                        </TableCell>
                                        <TableCell className="text-right font-medium">
                                          <SurchargeAmount
                                            amount={payment.amount}
                                            baseAmount={payment.base_amount}
                                            surchargeAmount={payment.surcharge_amount}
                                            surchargePercent={payment.surcharge_percent}
                                            testId={`recent-payment-amount-${payment.id || idx}`}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {(() => {
                                            const s = String(payment.status || '').toLowerCase();
                                            let cls = 'bg-red-100 text-red-700';
                                            let label = payment.status;
                                            if (['paid','completed','complete','succeeded'].includes(s)) {
                                              cls = 'bg-green-100 text-green-700';
                                              label = s === 'complete' ? 'completed' : payment.status;
                                            } else if (s === 'pending') {
                                              cls = 'bg-amber-100 text-amber-700';
                                            } else if (s === 'refunded') {
                                              // iter307er — Refunded chip on Recent Payments.
                                              cls = 'bg-purple-100 text-purple-700';
                                              label = 'Refunded';
                                            } else if (s === 'partially_refunded') {
                                              cls = 'bg-purple-50 text-purple-700';
                                              label = 'Partial refund';
                                            }
                                            return <Badge className={cls}>{label}</Badge>;
                                          })()}
                                        </TableCell>
                                      </TableRow>
                                      );
                                    })}
                                    {sIdx < visibleSections.length - 1 && (
                                      <TableRow className="hover:bg-transparent">
                                        <TableCell colSpan={5} className="p-0 h-2 border-0" />
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No payment history available</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Refunds Issued — every refund that's gone out to this
                  customer across all entity types (enrollment, tournament,
                  lane booking, etc.). Sorted most-recent-first. */}
              <Card data-testid="refunds-issued-card">
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="flex items-center gap-2">
                      <Undo2 className="h-5 w-5" />
                      Refunds Issued
                      {refunds.length > 0 && (
                        <span className="text-sm font-normal text-slate-500 ml-2">
                          Total: <span className="font-semibold text-orange-600" data-testid="refunds-total">
                            {cs()}{refundsTotal.toFixed(2)}
                          </span>
                          <span className="text-slate-400"> ({refunds.length} refund{refunds.length === 1 ? '' : 's'})</span>
                        </span>
                      )}
                    </CardTitle>
                  </div>
                  <CardDescription>
                    Refunds processed to this customer across enrollments, tournaments, lane rentals and other purchases.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {refundsLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  ) : refunds.length === 0 ? (
                    <div className="text-center py-8 text-slate-500" data-testid="refunds-empty-state">
                      <Undo2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No refunds issued to this customer.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table className="table-fixed w-full">
                        <colgroup>
                          <col style={{ width: '110px' }} />
                          <col />
                          <col style={{ width: '120px' }} />
                          <col style={{ width: '110px' }} />
                          <col />
                          <col style={{ width: '110px' }} />
                        </colgroup>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>For</TableHead>
                            <TableHead>Method</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead className="text-right">Receipt</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {refunds.map((r, idx) => {
                            // Friendly labels for each entity_type. Falls
                            // back to the raw string snake_case → Title Case.
                            const ENTITY_LABEL = {
                              enrollment: 'Enrollment',
                              tournament_registration: 'Tournament',
                              lane_booking: 'Lane Rental',
                              camp_registration: 'Camp',
                              coaching_session: 'Coaching Session',
                              small_group_registration: 'Small Group',
                            };
                            const entityLabel = ENTITY_LABEL[r.entity_type]
                              || String(r.entity_type || '').replace(/_/g, ' ');
                            // Stripe method renders as 'stripe' (lowercase),
                            // credit / cash are the other two values.
                            const methodColor =
                              r.method === 'stripe' ? 'bg-indigo-100 text-indigo-700' :
                              r.method === 'credit' ? 'bg-emerald-100 text-emerald-700' :
                              r.method === 'cash'   ? 'bg-amber-100 text-amber-700'   :
                              'bg-slate-100 text-slate-700';
                            return (
                              <TableRow key={r.id || idx} data-testid={`refund-row-${idx}`}>
                                <TableCell className="whitespace-nowrap text-slate-600">
                                  {r.refunded_at
                                    ? new Date(r.refunded_at).toLocaleDateString()
                                    : '-'}
                                </TableCell>
                                <TableCell className="truncate">
                                  <div className="flex flex-col">
                                    <span className="font-medium text-slate-900 capitalize">{entityLabel}</span>
                                    {r.entity_summary && (
                                      <span className="text-xs truncate">
                                        <EntityRefLink
                                          route={r.entity_route}
                                          entityType={r.entity_type}
                                          label={r.entity_summary}
                                          testId={`refund-for-${idx}`}
                                        />
                                      </span>
                                    )}
                                    {r.player_name && (
                                      <span className="text-xs text-slate-500">
                                        Player: <PlayerLink id={r.player_id} name={r.player_name} />
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge className={`${methodColor} capitalize`} data-testid={`refund-method-${idx}`}>
                                    {r.method || 'stripe'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-semibold text-orange-600" data-testid={`refund-amount-${idx}`}>
                                  −{cs()}{Number(r.amount || 0).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-slate-600 text-sm truncate" title={r.reason || ''}>
                                  {r.reason || <span className="text-slate-400 italic">No reason recorded</span>}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={sendingReceiptId === r.id}
                                    onClick={() => handleSendRefundReceipt(r)}
                                    className="text-xs text-blue-700 border-blue-200 hover:bg-blue-50"
                                    data-testid={`send-refund-receipt-${idx}`}
                                    title="Re-send refund receipt email"
                                  >
                                    {sendingReceiptId === r.id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <>
                                        <Mail className="h-3.5 w-3.5 mr-1" />
                                        Send
                                      </>
                                    )}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="mt-6">
            <ParentNotesSection parentId={parentId} />
          </TabsContent>

          {/* iter252 — Email Activity tab */}
          <TabsContent value="activity" className="mt-6">
            <EmailActivityTab userId={parentId} />
          </TabsContent>
        </Tabs>

        {/* Edit Parent Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit {parentForm.role === 'customer' ? 'Customer' : 'Parent'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={parentForm.name}
                  onChange={(e) => setParentForm({ ...parentForm, name: e.target.value })}
                  data-testid="edit-parent-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={parentForm.phone}
                  onChange={(e) => setParentForm({ ...parentForm, phone: e.target.value })}
                  data-testid="edit-parent-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-parent-role">Account Type</Label>
                <Select
                  value={parentForm.role || 'parent'}
                  onValueChange={(value) => setParentForm({ ...parentForm, role: value })}
                >
                  <SelectTrigger id="edit-parent-role" data-testid="edit-parent-role-trigger">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent" data-testid="edit-parent-role-option-parent">Parent</SelectItem>
                    <SelectItem value="customer" data-testid="edit-parent-role-option-customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Switches between <strong>Parent</strong> (manages enrolled players) and <strong>Customer</strong> (lane bookings only, no players). All other settings stay intact.
                </p>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Address</h4>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Street Address</Label>
                    <Input
                      value={parentForm.address}
                      onChange={(e) => setParentForm({ ...parentForm, address: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={parentForm.city}
                        onChange={(e) => setParentForm({ ...parentForm, city: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input
                        value={parentForm.state}
                        onChange={(e) => setParentForm({ ...parentForm, state: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ZIP</Label>
                      <Input
                        value={parentForm.zip}
                        onChange={(e) => setParentForm({ ...parentForm, zip: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Emergency Contact</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={parentForm.emergency_contact_name}
                      onChange={(e) => setParentForm({ ...parentForm, emergency_contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={parentForm.emergency_contact_phone}
                      onChange={(e) => setParentForm({ ...parentForm, emergency_contact_phone: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Textarea
                  value={parentForm.notes}
                  onChange={(e) => setParentForm({ ...parentForm, notes: e.target.value })}
                  placeholder="Internal notes about this parent..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveParent} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Enrollment Action Dialog */}
        <Dialog open={enrollmentActionDialog} onOpenChange={setEnrollmentActionDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {enrollmentAction === 'pause' && <PauseCircle className="h-5 w-5 text-amber-500" />}
                {enrollmentAction === 'cancel' && <XCircle className="h-5 w-5 text-red-500" />}
                {enrollmentAction === 'resume' && <PlayCircle className="h-5 w-5 text-green-500" />}
                {enrollmentAction === 'pause' && 'Pause Enrollment'}
                {enrollmentAction === 'cancel' && 'Cancel Enrollment'}
                {enrollmentAction === 'resume' && 'Resume Enrollment'}
              </DialogTitle>
            </DialogHeader>
            
            {selectedEnrollment && (
              <div className="space-y-4">
                {/* Enrollment Info */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedEnrollment.program_name}</p>
                  <p className="text-sm text-slate-500">{selectedEnrollment.class_name}</p>
                  <p className="text-sm text-blue-600 mt-1">
                    Player: {selectedEnrollment.player_name}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span>Fee: {cs()}{selectedEnrollment.monthly_fee}/mo</span>
                    <span className="capitalize">({selectedEnrollment.payment_frequency})</span>
                  </div>
                  {selectedEnrollment.credit_balance > 0 && (
                    <p className="text-sm text-green-600 mt-1">
                      Existing Credit: {cs()}{selectedEnrollment.credit_balance.toFixed(2)}
                    </p>
                  )}
                </div>
                
                {/* Pause Form */}
                {enrollmentAction === 'pause' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Pause Start Date *</Label>
                        <Input
                          type="date"
                          value={actionForm.pause_start_date}
                          onChange={(e) => {
                            setActionForm({ ...actionForm, pause_start_date: e.target.value });
                            setCreditCalculation(null);
                          }}
                          min={new Date().toISOString().split('T')[0]}
                          data-testid="pause-start-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Resume Date *</Label>
                        <Input
                          type="date"
                          value={actionForm.resume_date}
                          onChange={(e) => setActionForm({ ...actionForm, resume_date: e.target.value })}
                          min={actionForm.pause_start_date}
                          data-testid="pause-resume-date"
                        />
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={calculatePauseCredit}
                      disabled={calculatingCredit || !actionForm.pause_start_date}
                      data-testid="calculate-pause-credit-btn"
                    >
                      {calculatingCredit ? 'Calculating...' : 'Calculate Credit'}
                    </Button>
                  </div>
                )}
                
                {/* Cancel Form */}
                {enrollmentAction === 'cancel' && (
                  <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">Notice Period Required</p>
                          <p className="text-amber-700">
                            Cancellation requires 1 billing cycle notice (minimum 30 days).
                            {creditCalculation?.earliest_cancel_date && (
                              <span className="block mt-1">
                                Earliest cancel date: <strong>{formatDateOnly(creditCalculation.earliest_cancel_date, { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' })}</strong>
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Cancel Date</Label>
                      <Input
                        type="date"
                        value={actionForm.cancel_date}
                        onChange={(e) => {
                          setActionForm({ ...actionForm, cancel_date: e.target.value });
                          if (e.target.value) {
                            calculateCancelCredit(selectedEnrollment.id, e.target.value);
                          }
                        }}
                        data-testid="cancel-date"
                      />
                      <p className="text-xs text-slate-500">
                        Admin can pick any date — leave empty to use the earliest allowed date.
                      </p>
                    </div>

                    {/* Early-cancel warning + admin override (iter216).
                        Shown only when the picked date is before the
                        system-calculated earliest_cancel_date. Without
                        this checkbox the backend now rejects the
                        cancel with a 400 ("Set override_notice_period
                        =true to bypass"). */}
                    {actionForm.cancel_date && creditCalculation?.earliest_cancel_date && (
                      new Date(actionForm.cancel_date) < new Date(creditCalculation.earliest_cancel_date)
                    ) && (
                      <div
                        className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2"
                        data-testid="early-cancel-warning"
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-red-800">
                            <p className="font-medium">Cancelling BEFORE the notice period</p>
                            <p className="text-red-700 mt-1">
                              The selected date is earlier than the 30-day notice + current cycle.
                              This bypasses the standard cancellation policy and is logged in the audit trail.
                            </p>
                          </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-red-900 pl-7 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!actionForm.override_notice_period}
                            onChange={(e) =>
                              setActionForm({ ...actionForm, override_notice_period: e.target.checked })
                            }
                            data-testid="override-notice-checkbox"
                          />
                          <span>I confirm overriding the notice period (admin)</span>
                        </label>
                      </div>
                    )}

                    {/* Zero-credit override — admin can opt out of
                        issuing the calculated pro-rated credit (e.g.
                        mutually agreed cancel with no refund / when
                        invoice was unpaid and there's nothing to
                        credit). Resolves the production bug where an
                        unpaid kid still saw a calculated credit. */}
                    {creditCalculation && (creditCalculation.credit_amount || 0) > 0 && (
                      <label
                        className="flex items-start gap-2 p-3 rounded-lg border bg-slate-50 border-slate-200 cursor-pointer"
                        data-testid="zero-credit-block"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 text-slate-700 rounded"
                          checked={!!actionForm.override_credit}
                          onChange={(e) =>
                            setActionForm({ ...actionForm, override_credit: e.target.checked })
                          }
                          data-testid="zero-credit-checkbox"
                        />
                        <span className="text-sm text-slate-700">
                          <span className="font-medium">Do not issue credit (zero out the {cs()}{(creditCalculation.credit_amount || 0).toFixed(2)} above)</span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Use when the cancellation is mutually agreed and no refund / account credit should be issued (e.g. invoice was never paid).
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                )}
                
                {/* Resume Form */}
                {enrollmentAction === 'resume' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Resume Date</Label>
                      <Input
                        type="date"
                        value={actionForm.resume_date}
                        onChange={(e) => setActionForm({ ...actionForm, resume_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="resume-date"
                      />
                      <p className="text-xs text-slate-500">
                        Leave empty to resume immediately
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Credit Calculation Display */}
                {creditCalculation && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-800">Credit Calculation</p>
                        <div className="text-sm text-blue-700 mt-1 space-y-1">
                          <p>Billing Period: {new Date(creditCalculation.billing_period_start).toLocaleDateString()} - {new Date(creditCalculation.billing_period_end).toLocaleDateString()}</p>
                          {creditCalculation.classes_remaining !== undefined ? (
                            <p>Classes Remaining: {creditCalculation.classes_remaining} of {creditCalculation.total_classes_in_month}</p>
                          ) : (
                            <p>Days Remaining: {creditCalculation.days_remaining} of {creditCalculation.days_in_period}</p>
                          )}
                          <p className="font-medium text-lg" data-testid="credit-amount">
                            Credit: {cs()}{creditCalculation.credit_amount?.toFixed(2) || '0.00'}
                          </p>
                          {creditCalculation.is_end_of_cycle && (
                            <p className="text-green-600">✓ At end of billing cycle - no credit needed</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Reason — mandatory on Cancel (iter216), optional otherwise. */}
                <div className="space-y-2">
                  <Label>
                    Reason
                    {enrollmentAction === 'cancel' ? (
                      <span className="text-red-500 ml-1">*</span>
                    ) : (
                      <span className="text-slate-400 text-xs"> (Optional)</span>
                    )}
                  </Label>
                  <Textarea
                    value={actionForm.reason}
                    onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                    placeholder={enrollmentAction === 'cancel'
                      ? 'Required — record why this enrollment is being cancelled.'
                      : 'Enter reason for this action...'}
                    rows={2}
                    required={enrollmentAction === 'cancel'}
                    data-testid="action-reason"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEnrollmentActionDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleEnrollmentAction}
                disabled={
                  processingAction ||
                  (enrollmentAction === 'cancel' && (
                    !(actionForm.reason || '').trim() ||
                    (
                      actionForm.cancel_date &&
                      creditCalculation?.earliest_cancel_date &&
                      new Date(actionForm.cancel_date) < new Date(creditCalculation.earliest_cancel_date) &&
                      !actionForm.override_notice_period
                    )
                  ))
                }
                data-testid="confirm-action-btn"
                className={
                  enrollmentAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' :
                  enrollmentAction === 'pause' ? 'bg-amber-600 hover:bg-amber-700' :
                  'bg-green-600 hover:bg-green-700'
                }
              >
                {processingAction ? 'Processing...' : 
                  enrollmentAction === 'pause' ? 'Pause Enrollment' :
                  enrollmentAction === 'cancel' ? 'Cancel Enrollment' :
                  'Resume Enrollment'
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Inactivate Parent Dialog */}
        <Dialog open={inactivateDialog} onOpenChange={setInactivateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <UserX className="h-5 w-5" />
                Inactivate Parent Account
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              {loadingInactivateCheck ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-red-500 border-t-transparent"></div>
                </div>
              ) : activeEnrollmentsCheck?.can_inactivate ? (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Are you sure you want to inactivate this parent?</p>
                        <p className="text-sm text-amber-700 mt-1">
                          This will prevent {parent?.name} from logging in and accessing their account.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600">
                      <strong>Parent:</strong> {parent?.name}
                    </p>
                    <p className="text-sm text-slate-600">
                      <strong>Email:</strong> {parent?.email}
                    </p>
                    <p className="text-sm text-slate-600">
                      <strong>Children:</strong> {activeEnrollmentsCheck?.children_count || 0}
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700">
                      All administrators will be notified via email about this inactivation.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Cannot inactivate this parent</p>
                        <p className="text-sm text-red-700 mt-1">
                          This parent has active enrollments that must be cancelled first.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {activeEnrollmentsCheck?.active_enrollments?.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium text-slate-700">Active Enrollments to Cancel:</p>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {activeEnrollmentsCheck.active_enrollments.map((enrollment) => (
                          <div 
                            key={enrollment.id}
                            className="bg-white border rounded-lg p-3 flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium text-slate-800">{enrollment.player_name}</p>
                              <p className="text-sm text-slate-600">{enrollment.program_name} - {enrollment.class_name}</p>
                              <Badge className={
                                enrollment.status === 'paused' ? 'bg-orange-100 text-orange-700' :
                                enrollment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                              }>
                                {enrollment.status}
                              </Badge>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setInactivateDialog(false);
                                setActiveTab('enrollments');
                              }}
                            >
                              View
                            </Button>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-slate-500 mt-2">
                        Go to the Enrollments tab to cancel these enrollments first.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setInactivateDialog(false)}>
                Cancel
              </Button>
              {activeEnrollmentsCheck?.can_inactivate && (
                <Button 
                  onClick={handleInactivateParent}
                  disabled={processingInactivate}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-inactivate-btn"
                >
                  {processingInactivate ? 'Processing...' : 'Yes, Inactivate Parent'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bill Now / Bill With Credit confirmation */}
        <Dialog open={billDialog} onOpenChange={setBillDialog}>
          <DialogContent className="sm:max-w-md" data-testid="bill-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {billMethod === 'credit' ? (
                  <Sparkles className="h-5 w-5 text-purple-600" />
                ) : (
                  <Zap className="h-5 w-5 text-green-600" />
                )}
                {billMethod === 'credit' ? 'Bill with Account Credit' : 'Bill Now (Saved Card)'}
              </DialogTitle>
            </DialogHeader>
            {billItem && (
              <div className="space-y-3">
                <div className="text-xs bg-slate-50 border rounded-md p-3 space-y-0.5">
                  <div><strong>Player:</strong> {billItem.player_name}</div>
                  <div><strong>Description:</strong> {billItem.description}</div>
                  <div>
                    <strong>Amount:</strong> {cs()}{Number(billItem.amount || 0).toFixed(2)}
                  </div>
                </div>
                {billMethod === 'credit' ? (
                  <p className="text-sm text-slate-600">
                    This will deduct <strong>{cs()}{Number(billItem.amount || 0).toFixed(2)}</strong> from
                    the parent's available credit pool
                    {' '}({cs()}{(Number(billingInfo?.customer_credit_balance || 0) + Number(billingInfo?.credit_balance || 0)).toFixed(2)} total:
                    {' '}{cs()}{Number(billingInfo?.credit_balance || 0).toFixed(2)} enrollment credit +
                    {' '}{cs()}{Number(billingInfo?.customer_credit_balance || 0).toFixed(2)} account credit).
                    Enrollment credits are drained first, then account credit covers the remainder.
                    No card will be charged.
                  </p>
                ) : (
                  <p className="text-sm text-slate-600">
                    This will immediately charge <strong>{cs()}{Number(billItem.amount || 0).toFixed(2)}</strong> to
                    the parent's saved card
                    {billingInfo?.saved_payment_methods?.[0] && (
                      <>
                        {' '}(<span className="font-mono">{billingInfo.saved_payment_methods[0].brand} ···· {billingInfo.saved_payment_methods[0].last4}</span>)
                      </>
                    )}
                    . If the card is declined or requires authentication, the charge will fail and you'll be notified.
                  </p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setBillDialog(false)} disabled={billLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmBill}
                disabled={billLoading}
                className={billMethod === 'credit' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'}
                data-testid="confirm-bill-btn"
              >
                {billLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {billLoading ? 'Charging…' : `Charge ${cs()}${Number(billItem?.amount || 0).toFixed(2)}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save card on file (MOTO — admin keys card on customer's behalf) */}
        <SaveCardOnFileDialog
          open={saveCardOpen}
          onOpenChange={setSaveCardOpen}
          parent={parent ? { id: parent.id, name: parent.name, email: parent.email } : null}
          onSaved={async () => {
            // Refresh billing info so `has_saved_payment_method` flips true
            // and the "Bill Now" buttons appear immediately.
            try {
              const billingRes = await api.get(`/parents/${parentId}/billing-info`);
              setBillingInfo(billingRes.data);
            } catch {
              /* soft-fail — dialog already toasted success */
            }
          }}
        />

        {/* Consolidate credits confirmation */}
        <Dialog open={consolidateDialog} onOpenChange={setConsolidateDialog}>
          <DialogContent className="sm:max-w-md" data-testid="consolidate-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Consolidate credits into account credit
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                This will move <strong>{cs()}{Number(billingInfo?.credit_balance || 0).toFixed(2)}</strong> sitting
                across individual enrollments into the parent's unified
                <strong> account credit balance</strong>. The total credit doesn't
                change — it just becomes usable against any future payment
                (tournaments, camps, enrollments, or lanes).
              </p>
              <p className="text-xs text-slate-500">
                Every move is logged in the credit ledger for audit.
                New account credit balance will be{' '}
                <strong>
                  {cs()}{((Number(billingInfo?.customer_credit_balance || 0) + Number(billingInfo?.credit_balance || 0))).toFixed(2)}
                </strong>.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConsolidateDialog(false)} disabled={consolidateLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleConsolidateCredits}
                disabled={consolidateLoading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-consolidate-btn"
              >
                {consolidateLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {consolidateLoading ? 'Consolidating…' : 'Consolidate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Credit Adjust Dialog */}
        <Dialog open={creditAdjustOpen} onOpenChange={setCreditAdjustOpen}>
          <DialogContent className="sm:max-w-[440px]">
            <DialogHeader>
              <DialogTitle>Adjust Credit Balance</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600">
                Current balance: <span className="font-semibold">{cs()}{creditBalance.toFixed(2)}</span>
              </p>
              <div>
                <Label htmlFor="credit-adjust-amount">
                  Amount <span className="text-slate-400">(positive to credit, negative to debit)</span>
                </Label>
                <Input
                  id="credit-adjust-amount"
                  type="number"
                  step="0.01"
                  placeholder="e.g. 25.00 or -10.00"
                  value={creditAdjustAmount}
                  onChange={(e) => setCreditAdjustAmount(e.target.value)}
                  data-testid="credit-adjust-amount"
                />
              </div>
              <div>
                <Label htmlFor="credit-adjust-reason">Reason *</Label>
                <Textarea
                  id="credit-adjust-reason"
                  rows={3}
                  placeholder="e.g. Goodwill credit for missed session; Promo code redemption; Correction of over-credit from Feb 15"
                  value={creditAdjustReason}
                  onChange={(e) => setCreditAdjustReason(e.target.value)}
                  data-testid="credit-adjust-reason"
                />
                <p className="text-xs text-slate-400 mt-1">
                  This reason is visible to the customer in their credit history.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreditAdjustOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={submitCreditAdjust}
                disabled={creditAdjustSaving}
                data-testid="credit-adjust-submit"
              >
                {creditAdjustSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Adjustment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter332c v10 — Void Credit-Ledger Row Dialog.
            Soft-deletes an erroneous row from customer_credits history
            with documented reason. Readers exclude voided rows from
            all calculations. `adjustBalance` reverses the row's delta
            from customer_credits.balance — leave TRUE for erroneous
            entries (typical). Uncheck when voiding a row that was
            itself a compensating audit correction (e.g., Nikunj's
            "Adjusting to zero" row that fixed a previously missing
            refund journal — balance is already right without changes). */}
        <Dialog open={!!voidRow} onOpenChange={(open) => !open && closeVoidModal()}>
          <DialogContent className="sm:max-w-[520px]" data-testid="void-credit-dialog">
            <DialogHeader>
              <DialogTitle>Void Credit Row</DialogTitle>
            </DialogHeader>
            {voidRow && (
              <div className="space-y-4 py-2">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1">
                  <p className="text-sm font-medium text-slate-900">
                    {voidRow.description}
                  </p>
                  {voidRow.reason && (
                    <p className="text-xs text-slate-500">
                      Original reason: {voidRow.reason}
                    </p>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className={`text-sm font-semibold ${
                      voidRow.type === 'issued' ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {voidRow.type === 'issued' ? '+' : '-'}{cs()}{Math.abs(voidRow.amount).toFixed(2)}
                    </span>
                    <span className="text-xs text-slate-400">
                      {voidRow.timestamp
                        ? new Date(voidRow.timestamp).toLocaleDateString()
                        : '-'}
                    </span>
                  </div>
                </div>

                <div>
                  <Label htmlFor="void-reason">Reason for voiding *</Label>
                  <Textarea
                    id="void-reason"
                    rows={3}
                    placeholder="e.g. Duplicate audit entry; Manual adjustment that doubled the balance; Row created in error"
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    data-testid="void-reason-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Voided rows stay in the audit log with your name, timestamp, and this reason.
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    id="void-adjust-balance"
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                    checked={voidAdjustBalance}
                    onChange={(e) => setVoidAdjustBalance(e.target.checked)}
                    data-testid="void-adjust-balance-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="void-adjust-balance" className="cursor-pointer">
                      Also reverse this row from the balance
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Uncheck when this row was itself a compensating audit
                      correction (e.g., "Adjusting to zero"): balance is
                      already correct, voiding the row shouldn't undo it.
                    </p>
                    {/* iter333c (Feb 2026) — Balance preview so admins
                        can see exactly what happens BEFORE confirming.
                        Highlights in red when the new balance would go
                        negative — that's a strong signal to uncheck. */}
                    <div className="mt-2 rounded-md border border-slate-200 bg-white p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Current balance</span>
                        <span className="font-mono">
                          {cs()}{Number(creditSummary?.current_credit_balance || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-slate-500">New balance after void</span>
                        {(() => {
                          const cur = Number(creditSummary?.current_credit_balance || 0);
                          const delta = voidAdjustBalance ? -Number(voidRow.amount || 0) : 0;
                          const nb = cur + delta;
                          const isNeg = nb < -0.005;
                          return (
                            <span className={`font-mono ${isNeg ? 'text-red-600 font-semibold' : 'text-slate-900'}`}>
                              {cs()}{nb.toFixed(2)} {isNeg && '⚠︎'}
                            </span>
                          );
                        })()}
                      </div>
                      {voidAdjustBalance && (() => {
                        const cur = Number(creditSummary?.current_credit_balance || 0);
                        const nb = cur - Number(voidRow.amount || 0);
                        if (nb < -0.005) {
                          return (
                            <p className="text-red-600 text-[11px] mt-2 leading-tight">
                              This void will make the balance negative. If the row was a
                              compensating audit correction, uncheck the box above.
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={closeVoidModal}
                disabled={voidSaving}
                data-testid="void-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={submitVoid}
                disabled={voidSaving || !(voidReason || '').trim()}
                data-testid="void-submit"
              >
                {voidSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Void Row
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentDetail;
